/**
 * Inbox-style list of pending workflow approvals.
 *
 * Each row exposes Approve / Deny buttons. Deny opens a small inline
 * input for the required reason; approve fires immediately (note is
 * optional and intentionally surfaced as a follow-up affordance).
 */

import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import { Textarea } from '@/shared/components/ui/textarea'
import {
  useApprovePending,
  useDenyPending,
  usePendingApprovals,
} from '@/shared/hooks/use-workflows'
import { cn } from '@/shared/lib/utils'
import { useFormatters } from '@/shared/lib/formatting'
import type { PendingTransition } from '@/shared/lib/workflows/types'

export interface PendingApprovalsListProps {
  className?: string
  emptyText?: string
}

export function PendingApprovalsList({
  className,
  emptyText = 'No pending approvals.',
}: PendingApprovalsListProps) {
  const { data: rows, isLoading, error } = usePendingApprovals()

  if (isLoading) {
    return (
      <div className={cn('space-y-3', className)}>
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-20 animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    )
  }
  if (error) {
    return (
      <p className={cn('text-sm text-destructive', className)}>
        Could not load pending approvals.
      </p>
    )
  }
  if (!rows || rows.length === 0) {
    return (
      <p className={cn('text-sm text-muted-foreground', className)}>{emptyText}</p>
    )
  }
  return (
    <div className={cn('space-y-3', className)}>
      {rows.map((row) => (
        <PendingRow key={row.uuid} row={row} />
      ))}
    </div>
  )
}

function formatTimestamp(iso: string | null, formatDateTime: (iso: string) => string): string {
  if (!iso) return '-'
  try {
    return formatDateTime(iso)
  } catch {
    return iso
  }
}

function PendingRow({ row }: { row: PendingTransition }) {
  const approve = useApprovePending()
  const deny = useDenyPending()
  const { formatDateTime } = useFormatters()
  const [showDeny, setShowDeny] = useState(false)
  const [reason, setReason] = useState('')

  const onApprove = async () => {
    try {
      await approve.mutateAsync({ uuid: row.uuid })
      toast.success(`Approved “${row.action_label}”.`)
    } catch (err) {
      toast.error((err as Error)?.message || 'Approval failed.')
    }
  }

  const onDeny = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason.')
      return
    }
    try {
      await deny.mutateAsync({ uuid: row.uuid, reason })
      toast.success('Request denied.')
      setShowDeny(false)
    } catch (err) {
      toast.error((err as Error)?.message || 'Denial failed.')
    }
  }

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold">{row.action_label}</p>
          <p className="text-xs text-muted-foreground">
            requested by {row.requested_by_name} ·{' '}
            {formatTimestamp(row.created_at, formatDateTime)}
          </p>
          {row.reason_text && (
            <p className="mt-1 text-sm text-foreground/80">
              “{row.reason_text}”
            </p>
          )}
          {row.expires_at && (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
              expires {formatTimestamp(row.expires_at, formatDateTime)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDeny((v) => !v)}
          >
            Deny
          </Button>
          <Button
            size="sm"
            onClick={onApprove}
            disabled={approve.isPending}
          >
            {approve.isPending ? 'Approving…' : 'Approve'}
          </Button>
        </div>
      </div>
      {showDeny && (
        <div className="mt-3 flex flex-col gap-2 border-t border-border pt-3">
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for denial (visible to the requester)"
            rows={2}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeny(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={onDeny}
              disabled={deny.isPending}
            >
              {deny.isPending ? 'Denying…' : 'Confirm denial'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}
